import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { RulesEngineFormValues } from '../rules-engine-form-page-old';
import { SectionHeader } from '../../../../shared/section-header';
import UsersAutocomplete from '../../../../shared/users-autocomplete';
import GroupsAutocomplete from '../../../../shared/groups-autocomplete';
import { useState } from 'react';
import AvatarChip from '../../../../shared/avatar-chip';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface PersonalCalendarFiltersStepProps {
  control: Control<RulesEngineFormValues>;
  watch: UseFormWatch<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  clearErrors: UseFormClearErrors<RulesEngineFormValues>;
}

export default function RulesEnginePersonalCalendarFiltersStep({
  control,
  watch,
  setValue,
  errors,
  clearErrors,
}: PersonalCalendarFiltersStepProps) {
  const [userAutocomplete, setUserAutocomplete] = useState('');
  const [groupAutocomplete, setGroupAutocomplete] = useState('');

  const hasPersonalCalendarFilter = watch(
    'personalCalendarFilter.hasPersonalCalendarFilter'
  );
  const sharedToSpecificUsers =
    watch('personalCalendarFilter.sharedToSpecificUsers') || [];
  const sharedToGroups = watch('personalCalendarFilter.sharedToGroups') || [];

  const handleClearFilters = () => {
    setValue('personalCalendarFilter.sharedToUserExternalToDomain', false);
    setValue('personalCalendarFilter.sharedToUserInternalToDomain', false);
    setValue('personalCalendarFilter.sharedToSpecificUsers', []);
    setValue('personalCalendarFilter.sharedToGroups', []);
    clearErrors([
      'personalCalendarFilter.sharedToUserExternalToDomain',
      'personalCalendarFilter.sharedToUserInternalToDomain',
      'personalCalendarFilter.sharedToSpecificUsers',
      'personalCalendarFilter.sharedToGroups',
    ]);
  };

  return (
    <Stack gap={2} width={'100%'}>
      <SectionHeader>
        Personal Calendar Filters{' '}
        <span style={{ fontWeight: 400 }}>(optional)</span>
      </SectionHeader>
      <Typography variant="caption">
        You can optionally provide a filter on calendar sharing information.
        This will act as an "and" condition with other filters.
      </Typography>

      <Box>
        <Controller
          name="personalCalendarFilter.hasPersonalCalendarFilter"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              control={
                <Checkbox
                  {...field}
                  checked={!!field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    if (!e.target.checked) {
                      handleClearFilters();
                    }
                  }}
                />
              }
              label="Filter By Calendar Sharing"
            />
          )}
        />
        {errors.personalCalendarFilter?.hasPersonalCalendarFilter && (
          <Typography color="error" variant="caption" sx={{ display: 'block' }}>
            {errors.personalCalendarFilter.hasPersonalCalendarFilter.message}
          </Typography>
        )}
      </Box>

      <Box sx={{ pl: '16px' }}>
        <Stack gap={2}>
          <Typography variant="body1" fontWeight={500}>
            Shared To
          </Typography>
          <Controller
            name="personalCalendarFilter.sharedToUserExternalToDomain"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!field.value} />}
                label="User external to the domain"
                disabled={!hasPersonalCalendarFilter}
              />
            )}
          />
          <Controller
            name="personalCalendarFilter.sharedToUserInternalToDomain"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!field.value} />}
                label="User internal to the domain"
                disabled={!hasPersonalCalendarFilter}
              />
            )}
          />

          <Box sx={{ maxWidth: 530 }}>
            <UsersAutocomplete
              label="Specific User"
              autocompleteValue={userAutocomplete}
              setAutoCompleteValue={setUserAutocomplete}
              onSelection={(value) => {
                if (value && !sharedToSpecificUsers.includes(value)) {
                  setValue('personalCalendarFilter.sharedToSpecificUsers', [
                    ...sharedToSpecificUsers,
                    value,
                  ]);
                }
                setUserAutocomplete('');
              }}
              disabled={!hasPersonalCalendarFilter}
            />
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {sharedToSpecificUsers.map((user) => (
                <AvatarChip
                  key={user}
                  label={user}
                  email={user}
                  type="User"
                  onDelete={() =>
                    setValue(
                      'personalCalendarFilter.sharedToSpecificUsers',
                      sharedToSpecificUsers.filter((u) => u !== user)
                    )
                  }
                />
              ))}
            </Stack>
          </Box>

          <Box sx={{ maxWidth: 530 }}>
            <GroupsAutocomplete
              autocompleteValue={groupAutocomplete}
              setAutoCompleteValue={setGroupAutocomplete}
              onSelection={(value) => {
                if (value && !sharedToGroups.includes(value)) {
                  setValue('personalCalendarFilter.sharedToGroups', [
                    ...sharedToGroups,
                    value,
                  ]);
                }
                setGroupAutocomplete('');
              }}
              disabled={!hasPersonalCalendarFilter}
            />
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {sharedToGroups.map((group) => (
                <AvatarChip
                  key={group}
                  label={group}
                  email={group}
                  type="Group"
                  onDelete={() =>
                    setValue(
                      'personalCalendarFilter.sharedToGroups',
                      sharedToGroups.filter((g) => g !== group)
                    )
                  }
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
}
